import React, { useEffect, useState } from "react";
import {
  BetaForm as useBetaForm,
  ButtonBase,
  ModalBase,
  TextInputBase,
} from "@reusejs/react";
import { CurrencyRupeeIcon } from "@heroicons/react/outline";
import ConfirmBidModal from "./confirmBidModal";
import { DateTime } from "luxon";
import { placeBid } from "services";
import { useSelector } from "react-redux";

export default function StickyFooter(props: any) {
  const user = useSelector((state: any) => {
    return state.bidderAuth.user;
  });

  const form = useBetaForm({
    bidding_value: "",
  });

  const form_constraints_rp = {
    bidding_value: {
      presence: {
        allowEmpty: false,
        message:
          "can't be blank and can't contain any special character or alphanumeric values",
      },
      numericality: {
        onlyInteger: true,
        greaterThanOrEqualTo:
          typeof props.amount === "string"
            ? parseInt(props.amount)
            : props.amount,
        lessThan:
          typeof props.amount === "string"
            ? parseInt(props.amount) + parseInt(props.amount)
            : props.amount + props.amount,
        notLessThan:
          "^Attention: Your bid must be below the threshold value. Please revise and resubmit",
      },
    },
  };

  const form_constraints_no_rp = {
    bidding_value: {
      presence: {
        allowEmpty: false,
        message:
          "can't be blank and can't contain any special character or alphanumeric values",
      },
      numericality: {
        onlyInteger: true,
        greaterThan:
          typeof props.amount === "string"
            ? parseInt(props.amount)
            : props.amount,
        lessThan:
          typeof props.amount === "string"
            ? parseInt(props.amount) + parseInt(props.amount)
            : props.amount + props.amount,
        notLessThan:
          "^Attention: Your bid must be below the threshold value. Please revise and resubmit",
      },
    },
  };

  useEffect(() => {
    if (props.biddingType === "rp") {
      form.setValidationRules(form_constraints_rp);
    } else {
      form.setValidationRules(form_constraints_no_rp);
    }
  }, [props.amount, props.biddingType]);

  const handlePlaceBidAfterConfirmation = async (biddingValue: number) => {
    try {
      const current_date_object = DateTime.now().setZone("Asia/Kolkata");
      const payload = {
        event_type: "user_has_placed_bid",
        event_uuid: props.id,
        bidding_amount:
          typeof biddingValue === "string"
            ? parseInt(biddingValue)
            : biddingValue,
        frontend_timestamp: current_date_object.toMillis(),
      };
      await placeBid(payload);
    } catch (error: any) {
      console.log("check here err:", error);
    }
  };

  const openModal = async (
    connectionStatus: string,
    bidding: number,
    title: string,
    message: string,
    color: string
  ) => {
    const ConfirmBidModalWrapper = (props: any) => {
      return (
        <ConfirmBidModal
          connectionStatus={connectionStatus}
          biddingAmount={bidding}
          heading={title}
          description={message}
          callback={() => handlePlaceBidAfterConfirmation(bidding)}
          color={color}
          {...props}
        />
      );
    };

    await ModalBase(
      {
        content: ConfirmBidModalWrapper,
        timeout: 0,
        backgroundColor: "bg-black/30",
        backgroundOpacity: "opacity-70",
        modalBaseClasses: {
          background: "bg-white",
          border: "border-2 rounded-lg",
          alignment: "text-left overflow-visible",
          padding: "p-0",
          small: "sm:my-8 sm:w-full sm:max-w-md",
        },
      },
      1000,
      "__next"
    );
  };

  const handlePlaceBid = () => {
    try {
      console.log(form.validate());
      if (form.validate()) {
        openModal(
          props.connectionStatus,
          form.value.bidding_value,
          "Confirm Bid Value",
          "Please confirm the bidding amount!",
          "bg-blue-50 text-blue-500"
        );
      }
    } catch (error) {
      console.log("chec khere error", error);
    }
  };

  return (
    <>
      <div className="fixed inset-x-0 sm:left-64 sm:right-0 bottom-0">
        <div className="bg-blue-500">
          <div className="mx-auto max-w-2xl py-3 px-4">
            <div className="flex flex-wrap items-center justify-between">
              <div className="w-full">
                <TextInputBase
                  name={"bidding_value"}
                  onChange={(val) => form.setField("bidding_value", val)}
                  placeholder={
                    props.biddingType === "rp"
                      ? !props.latestBid &&
                        parseInt(props.amount) === parseInt(props.reservePrice)
                        ? `Enter amount >= ${props.amount}`
                        : `Enter amount > ${props.amount}`
                      : `Enter amount > ${props.amount}`
                  }
                  type={"number"}
                  value={form.getField("bidding_value")}
                  htmlFor={"bidding_value"}
                  textInputBaseClasses={{
                    padding: "pl-8 py-3",
                    wrapper: "relative rounded-md shadow-sm w-full",
                  }}
                  textInputPrefix={
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
                      <CurrencyRupeeIcon
                        height="20"
                        width="20"
                        className="text-gray-500 font-semibold"
                      />
                    </div>
                  }
                  textInputSuffix={
                    <div className="absolute inset-y-0 right-0 flex items-center">
                      <ButtonBase
                        variant="primary"
                        type="submit"
                        label="Place Bid"
                        onClick={() => handlePlaceBid()}
                        buttonBaseClasses={{
                          font: "text-sm font-semibold",
                          backgroundColor:
                            "bg-green-500 hover:bg-green-600 disabled:bg-gray-400",
                          textColor: "text-white",
                          borderRadius: "rounded-r-md",
                          padding: "px-4 py-3.5 md:py-3",
                        }}
                        disabled={
                          props.status !== "live" ||
                          ["Offline", "Connecting"].includes(
                            props.connectionStatus
                          ) ||
                          props?.isTimeEnded === 1
                        }
                      />
                    </div>
                  }
                  error={
                    form.errors.get("bidding_value") ? (
                      <p className="mt-2 text-md text-red-800">
                        {form.errors.get("bidding_value")[0]}
                      </p>
                    ) : null
                  }
                  disabled={
                    props.status !== "live" ||
                    ["Offline", "Connecting"].includes(
                      props.connectionStatus
                    ) ||
                    props?.isTimeEnded === 1
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
